import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  useInteractionTemplates,
  useCreateInteractionTemplate,
  useDeleteInteractionTemplate,
  DEFAULT_TEMPLATES,
} from '@/hooks/use-interaction-templates'
import type { InteractionType, InteractionQuality } from '@/lib/types'

const INTERACTION_TYPES: { value: InteractionType; label: string }[] = [
  { value: 'call', label: 'Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'coffee', label: 'Coffee' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'video_call', label: 'Video Call' },
  { value: 'other', label: 'Other' },
]

const QUALITY_OPTIONS: { value: InteractionQuality; label: string; emoji: string }[] = [
  { value: 'great', label: 'Great', emoji: '😊' },
  { value: 'good', label: 'Good', emoji: '🙂' },
  { value: 'neutral', label: 'Neutral', emoji: '😐' },
  { value: 'poor', label: 'Poor', emoji: '😞' },
]

interface TemplateManagerProps {
  open: boolean
  onClose: () => void
}

export function TemplateManager({ open, onClose }: TemplateManagerProps) {
  const { t } = useTranslation()
  const { data: customTemplatesData } = useInteractionTemplates()
  const createTemplate = useCreateInteractionTemplate()
  const deleteTemplate = useDeleteInteractionTemplate()

  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState<InteractionType>('coffee')
  const [quality, setQuality] = useState<InteractionQuality | ''>('')
  const [icon, setIcon] = useState('')

  const customTemplates = customTemplatesData?.items ?? []

  function handleCreate(e: FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return

    createTemplate.mutate(
      {
        name: trimmed,
        type,
        quality: quality || null,
        icon: icon.trim() || null,
      },
      {
        onSuccess: () => {
          setName('')
          setType('coffee')
          setQuality('')
          setIcon('')
          setShowForm(false)
        },
      },
    )
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
      <div className="bg-surface-container-low flex max-h-[80vh] w-full max-w-md flex-col rounded-xl shadow-lg">
        {/* Header */}
        <div className="border-outline-variant/20 flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold">{t('templates.manage', 'Manage Templates')}</h2>
          <button onClick={onClose} className="text-on-surface-variant hover:text-foreground p-1">
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {/* Custom templates */}
          {customTemplates.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-on-surface-variant text-xs font-medium tracking-wider uppercase">
                {t('templates.custom', 'Your Templates')}
              </h3>
              {customTemplates.map((tmpl) => (
                <div
                  key={tmpl.id}
                  className="bg-surface-container flex items-center justify-between rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    {tmpl.icon && <span className="text-base">{tmpl.icon}</span>}
                    <div>
                      <p className="text-sm font-medium">{tmpl.name}</p>
                      <p className="text-on-surface-variant text-xs">{tmpl.type}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteTemplate.mutate(tmpl.id)}
                    className="text-on-surface-variant hover:text-destructive p-1 transition-colors"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new template form */}
          {showForm ? (
            <form
              onSubmit={handleCreate}
              className="border-outline-variant/20 space-y-3 rounded-lg border p-3"
            >
              <div>
                <label className="text-on-surface-variant text-xs font-medium">
                  {t('templates.name', 'Name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Coffee date"
                  className="bg-surface-container border-outline-variant/30 focus:ring-primary/50 mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-on-surface-variant text-xs font-medium">
                  {t('templates.icon', 'Icon (emoji)')}
                </label>
                <input
                  type="text"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  placeholder="☕"
                  maxLength={4}
                  className="bg-surface-container border-outline-variant/30 focus:ring-primary/50 mt-1 w-20 rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
                />
              </div>

              <div>
                <label className="text-on-surface-variant text-xs font-medium">
                  {t('templates.type', 'Type')}
                </label>
                <div className="mt-1 grid grid-cols-4 gap-1.5">
                  {INTERACTION_TYPES.map((it) => (
                    <button
                      key={it.value}
                      type="button"
                      onClick={() => setType(it.value)}
                      className={cn(
                        'rounded-lg border px-2 py-1.5 text-xs transition-colors',
                        type === it.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-outline-variant/30 hover:bg-surface-container-high',
                      )}
                    >
                      {it.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-on-surface-variant text-xs font-medium">
                  {t('templates.quality', 'Default Quality')}
                </label>
                <div className="mt-1 flex gap-2">
                  {QUALITY_OPTIONS.map((q) => (
                    <button
                      key={q.value}
                      type="button"
                      onClick={() => setQuality(quality === q.value ? '' : q.value)}
                      className={cn(
                        'flex items-center gap-1 rounded-lg border px-2 py-1.5 text-xs transition-colors',
                        quality === q.value
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-outline-variant/30 hover:bg-surface-container-high',
                      )}
                    >
                      <span>{q.emoji}</span>
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowForm(false)}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" size="sm" disabled={!name.trim() || createTemplate.isPending}>
                  {createTemplate.isPending
                    ? t('common.saving', 'Saving...')
                    : t('common.save', 'Save')}
                </Button>
              </div>
            </form>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => setShowForm(true)}>
              <Plus className="mr-2 size-4" />
              {t('templates.add', 'Add Custom Template')}
            </Button>
          )}

          {/* Default presets (read-only info) */}
          <div className="space-y-2">
            <h3 className="text-on-surface-variant text-xs font-medium tracking-wider uppercase">
              {t('templates.presets', 'Default Presets')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_TEMPLATES.map((tmpl) => (
                <span
                  key={tmpl.id}
                  className="bg-surface-container inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs"
                >
                  {tmpl.icon && <span>{tmpl.icon}</span>}
                  {tmpl.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
